import { useState, useEffect } from "react";
import { API_URL } from "../../context/AuthContext";
import {
  Box,
  Typography,
  Alert,
  Button,
  Card,
  CardMedia,
  CardActions,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import type { ProductData } from "./Dashboard";

interface ImagesSectionProps {
  product: ProductData;
  token: string | null;
  onChange: () => void;
}

export default function ImagesSection({ product, token, onChange }: ImagesSectionProps) {
  const [images, setImages] = useState(product.images);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    setImages([...product.images].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
  }, [product]);

  const headers = { Authorization: `Bearer ${token}` };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      await fetch(`${API_URL}/api/products/${product.id}/images`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      setMsg("Zdjęcie przesłane");
      onChange();
    } catch {
      setMsg("Błąd przesyłania");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Usunąć to zdjęcie?")) return;
    try {
      await fetch(`${API_URL}/api/images/${id}`, { method: "DELETE", headers });
      setMsg("Usunięto zdjęcie");
      onChange();
    } catch {
      setMsg("Błąd usuwania");
    }
  };

  const handleSetMain = async (img: { id: number }) => {
    try {
      const res = await fetch(`${API_URL}/api/products/${product.id}/hero-image`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ image_id: img.id }),
      });
      if (!res.ok) throw new Error();
      setMsg("Ustawiono zdjęcie główne");
      onChange();
    } catch {
      setMsg("Błąd ustawiania zdjęcia głównego");
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {msg && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMsg("")}>{msg}</Alert>}
      <Typography variant="caption" sx={{ color: "#aaa", mb: 1, display: "block" }}>
        Zaznacz gwiazdkę, aby ustawić zdjęcie jako główne na stronie głównej.
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {images.map((img) => {
          const isMain = product.hero_image_id === img.id;
          return (
            <Card key={img.id} sx={{ width: 200, backgroundColor: "#222", border: isMain ? "2px solid #1976d2" : "1px solid #444" }}>
              <CardMedia
                component="img"
                height="140"
                image={`${API_URL}${img.url}`}
                alt=""
                sx={{ objectFit: "contain", backgroundColor: "#1a1a1a" }}
              />
              <CardActions sx={{ justifyContent: "center", gap: 0.5 }}>
                <IconButton size="small" onClick={() => handleSetMain(img)} sx={{ color: isMain ? "#ffc107" : "#666" }}>
                  {isMain ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                </IconButton>
                <Button size="small" startIcon={<DeleteIcon />} onClick={() => handleDelete(img.id)} sx={{ color: "#f44336" }}>
                  Usuń
                </Button>
              </CardActions>
            </Card>
          );
        })}
      </Box>
      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          component="label"
          disabled={uploading}
          sx={{ color: "#1976d2", borderColor: "#1976d2" }}
        >
          {uploading ? "Przesyłanie..." : "Dodaj zdjęcie"}
          <input type="file" accept="image/*" hidden onChange={handleUpload} />
        </Button>
      </Box>
    </Box>
  );
}
